import { DependencyList, useCallback, useEffect, useState } from 'react';

/** Returns the scroll-left offset where each panel begins. */
export function computePanelStarts(panelScroller: HTMLElement): number[] {
    const cards = Array.from(panelScroller.children) as HTMLElement[];
    if (cards.length === 0) {
        return [0];
    }

    const viewport = panelScroller.clientWidth;
    if (panelScroller.scrollWidth <= viewport) {
        return [0];
    }

    // Use bounding rects, not offsetLeft — offsetLeft misreports inside MUI Collapse wrappers.
    const { left: containerLeft } = panelScroller.getBoundingClientRect();
    const { scrollLeft } = panelScroller;

    const starts = [0];
    cards.forEach((card) => {
        const rect = card.getBoundingClientRect();
        const left = rect.left - containerLeft + scrollLeft;
        const right = left + rect.width;
        const panel = starts[starts.length - 1];
        if (right > panel + viewport && left > panel) {
            starts.push(left);
        }
    });
    return starts;
}

/**
 * Tracks pagination state for a horizontally-scrollable panel scroller. Recomputes panel
 * boundaries when the scroller resizes or when its content changes.
 */
export default function useCarouselPanels(contentDep: DependencyList[number]) {
    const [panelScroller, setPanelScroller] = useState<HTMLDivElement | null>(null);
    const [panelStarts, setPanelStarts] = useState<number[]>([0]);
    const [currentPanelIdx, setCurrentPanelIdx] = useState(0);

    const panelScrollerRef = useCallback((node: HTMLDivElement | null) => {
        setPanelScroller(node);
    }, []);

    useEffect(() => {
        if (!panelScroller) {
            return undefined;
        }
        const recompute = () => setPanelStarts(computePanelStarts(panelScroller));
        const observer = new ResizeObserver(recompute);
        const observeChildren = () => {
            observer.disconnect();
            observer.observe(panelScroller);
            Array.from(panelScroller.children).forEach((child) => observer.observe(child));
            recompute();
        };
        const childListObserver = new MutationObserver(observeChildren);
        observeChildren();
        childListObserver.observe(panelScroller, { childList: true });
        return () => {
            observer.disconnect();
            childListObserver.disconnect();
        };
    }, [contentDep, panelScroller]);

    // Clamp when panels shrink (e.g. a card was removed).
    useEffect(() => {
        setCurrentPanelIdx((prev) => Math.min(prev, Math.max(0, panelStarts.length - 1)));
    }, [panelStarts]);

    const scrollToPanel = (idx: number) => {
        const clamped = Math.min(Math.max(idx, 0), Math.max(0, panelStarts.length - 1));
        panelScroller?.scrollTo({ left: panelStarts[clamped], behavior: 'smooth' });
        setCurrentPanelIdx(clamped);
    };

    return {
        panelScrollerRef,
        totalPanels: panelStarts.length,
        currentPanelIdx,
        canPrev: currentPanelIdx > 0,
        canNext: currentPanelIdx < panelStarts.length - 1,
        scrollToPanel,
    };
}
